import { useState } from "react";

import { ApiError } from "@/api/client";
import { ReportCard } from "@/components/ReportCard";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useGenerateReport, useReports } from "@/hooks/useReports";

const PER_PAGE = 20;

export function ReportsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [page, setPage] = useState(1);
  const { data, isPending, error } = useReports({ page, perPage: PER_PAGE });
  const generate = useGenerateReport();

  const listError = error
    ? error instanceof ApiError
      ? error.message
      : "Error al cargar los reportes."
    : null;

  let generateError: string | null = null;
  let conflictReportId: string | null = null;
  if (generate.isError) {
    if (generate.error instanceof ApiError) {
      generateError = generate.error.message;
      const body = generate.error.body as
        | { error?: { code?: string; report_id?: string } }
        | undefined;
      if (body?.error?.code === "report_already_exists") {
        conflictReportId = body.error.report_id ?? null;
      }
    } else {
      generateError = "Error al solicitar la generación.";
    }
  }

  const handleGenerate = (force = false) => {
    generate.mutate({ report_type: "monthly", force });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Reportes</h1>
          <p className="text-sm text-muted-foreground">
            Reportes mensuales del cliente. Se generan automáticamente el
            primer día de cada mes; también puedes solicitar uno on-demand.
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => handleGenerate(false)}
            disabled={generate.isPending}
          >
            {generate.isPending ? "Solicitando…" : "Generar ahora"}
          </Button>
        )}
      </div>

      {generate.isSuccess && (
        <div
          role="status"
          className="rounded-md border border-green-500/40 bg-green-50 px-3 py-2 text-sm text-green-900"
        >
          Reporte solicitado. Se mostrará en la lista cuando esté listo (suele
          tomar 10–30 segundos).
        </div>
      )}

      {generateError && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {generateError}
          {conflictReportId && isAdmin && (
            <div className="mt-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleGenerate(true)}
              >
                Regenerar (force)
              </Button>
            </div>
          )}
        </div>
      )}

      {listError && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {listError}
        </div>
      )}

      {isPending && !data ? (
        <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
          Cargando reportes…
        </div>
      ) : data && data.items.length === 0 ? (
        <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
          Sin reportes generados todavía.
        </div>
      ) : data ? (
        <>
          <div className="space-y-3">
            {data.items.map((report) => (
              <ReportCard key={report.id} report={report} />
            ))}
          </div>
          {data.total > PER_PAGE && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <Button
                variant="ghost"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </Button>
              <span>
                Página {data.page} de {Math.ceil(data.total / PER_PAGE)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={page * PER_PAGE >= data.total}
                onClick={() => setPage((p) => p + 1)}
              >
                Siguiente
              </Button>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
